import { useState } from 'react';
import {
  Avatar,
  Dialog,
  DialogActions,
  DialogContent,
  Drawer,
  Fade,
  IconButton,
  Paper
} from '@mui/material';
import { useIntl } from 'react-intl';
import { DataGrid, useGridApiRef } from '@mui/x-data-grid';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContentText from '@mui/material/DialogContentText';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import MDBox from 'components/MDBox';
import {
  useBulkDeleteAiModelsMutation,
  useCreateAiModelMutation,
  useGetAiModelsQuery,
  useToggleFavoriteMutation,
  useUpdateAiModelMutation
} from 'services/aiModelApi';
import './aiModel.css';
import AiModelDetails from 'pages/aiModels/components/AiModelDetails';
import AiModelCustomToolbar from 'pages/aiModels/components/AiModelCustomToolbar';
import { useNotification } from 'hook/useNotification';
import MDButton from 'components/MDButton';
import { buildImageUrl } from 'services/imageResourceApi';

/**
 * Component for managing AI Models, displaying list, adding, updating,
 * duplicating, and deleting models. Uses DataGrid for listing and Drawer for editing.
 */
export default function AiModels() {
  const { addNotification } = useNotification();
  const { formatMessage: msg } = useIntl();
  const apiRef = useGridApiRef();

  const { data: models = [], error: modelsError, isLoading: modelsLoading } = useGetAiModelsQuery();
  const [bulkDeleteAiModel] = useBulkDeleteAiModelsMutation();
  const [createAiModel] = useCreateAiModelMutation();
  const [updateAiModel] = useUpdateAiModelMutation();
  const [toggleFavorite] = useToggleFavoriteMutation();

  const [selectedModel, setSelectedModel] = useState(null);
  const [selectionModel, setSelectionModel] = useState([]);
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [detailDrawerOpen, setDetailDrawerOpen] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [confirmCloseFormDialogOpen, setConfirmCloseFormDialogOpen] = useState(false);

  const columns = [
    {
      field: 'favorite',
      headerName: msg({ id: `page.aiModels.table.column.favorite` }),
      width: 80,
      cellClassName: 'no-outline-cell',
      renderCell: (params) => (
        <IconButton
          onClick={(e) => {
            e.stopPropagation();
            handleToggleFavorite(params.row.id);
          }}
          color={params.row.favorite ? 'warning' : 'default'}
        >
          {params.row.favorite ? <StarIcon /> : <StarBorderIcon />}
        </IconButton>
      )
    },
    {
      field: 'id',
      headerName: msg({ id: `page.aiModels.table.column.id` }),
      disableColumnMenu: true,
      minWidth: 50,
      maxWidth: 50
    },
    {
      field: 'icon',
      headerName: msg({ id: `page.aiProviders.table.column.icon` }),
      minWidth: 70,
      maxWidth: 70,
      display: 'flex',
      alignItems: 'center',
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => {
        // Use the provider's code as the image code
        const imageUrl = buildImageUrl(params.row.provider.code, 'aiproviders');
        return (
          <Avatar
            src={imageUrl}
            alt={params.row.provider.code}
            sx={{ width: '30px' }}
            variant="rounded"
          >
            {params.row.code.charAt(0).toUpperCase()}
          </Avatar>
        );
      }
    },
    {
      field: 'code',
      headerName: msg({ id: `page.aiModels.table.column.code` }),
      minWidth: 300,
      maxWidth: 300
    },
    {
      field: 'label',
      headerName: msg({ id: `page.aiModels.table.column.label` }),
      minWidth: 300,
      maxWidth: 300
    },
    {
      field: 'description',
      headerName: msg({ id: `page.aiModels.table.column.description` }),
      minWidth: 300,
      flex: 1
    },
    {
      field: 'temperature',
      headerName: msg({ id: `page.aiModels.table.column.temperature` }),
      minWidth: 120,
      maxWidth: 120,
      type: 'number'
    },
    {
      field: 'maxTokens',
      headerName: msg({ id: `page.aiModels.table.column.maxTokens` }),
      minWidth: 120,
      maxWidth: 150,
      type: 'number'
    },
    {
      field: 'provider',
      headerName: msg({ id: `page.aiModels.table.column.provider` }),
      minWidth: 150,
      maxWidth: 200,
      valueGetter: (params) => params?.label || ''
    }
  ];

  const toggleDrawer = (open) => () => {
    if (isFormDirty) {
      setConfirmCloseFormDialogOpen(true);
    } else {
      setDetailDrawerOpen(open);
    }
  };

  const handleCloseDrawer = () => {
    setDetailDrawerOpen(false);
    setConfirmCloseFormDialogOpen(false);
  };

  const handleCancelClose = () => {
    setConfirmCloseFormDialogOpen(false);
  };

  const handleFieldChange = (dirty) => {
    setIsFormDirty(dirty);
  };

  const handleRowClick = (params) => {
    setSelectedModel(params.row);
    setSelectedRowId(params.id);
    setDetailDrawerOpen(true);
  };

  const handleSelectionModelChange = (newSelection) => {
    setSelectionModel(newSelection);
  };

  const handleRowSelectionModelChange = (rowSelectionModel) => {
    setRowSelectionModel(rowSelectionModel);
  };

  const handleAdd = () => {
    setSelectedModel(null);
    setDetailDrawerOpen(true);
  };

  const handleDelete = async () => {
    if (selectedRows) {
      bulkDeleteAiModel(selectedRows.map((row) => row.id))
        .unwrap()
        .then(() => {
          addNotification({
            type: 'success',
            title: msg({ id: 'notification.success.title' }),
            message: msg({ id: 'page.aiModels.delete.success' })
          });
          clearModelSelected();
        })
        .catch((error) => {
          console.error(error);
          addNotification({
            type: 'error',
            title: msg({ id: 'notification.error.title' }),
            message: msg({ id: 'page.aiModels.delete.error' })
          });
        });
    }
  };

  const handleDuplicate = () => {
    const toDuplicate = selectedRows[0];
    if (toDuplicate) {
      setSelectedModel({ ...toDuplicate, id: null, code: `${toDuplicate.code}_COPY` });
      setDetailDrawerOpen(true);
    }
  };

  const handleFormSubmit = (data) => {
    if (!data.id) {
      handleCreate(data);
    } else {
      handleUpdate(data);
    }
    setDetailDrawerOpen(false);
  };

  const handleToggleFavorite = (id) => {
    toggleFavorite(id)
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.aiModels.favorite.toggle.success' })
        });
      })
      .catch(() => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.aiModels.favorite.toggle.error' })
        });
      });
  };

  const selectedRows = rowSelectionModel.map((id) => models.find((model) => model.id === id));

  if (modelsError)
    try {
      addNotification({
        type: 'error',
        title: msg({ id: 'notification.warning.title' }),
        message: msg({ id: 'page.aiModels.fetch.error' })
      });
    } catch {
      //ignore
    }

  return (
    <Fade in timeout={(1000, 500)}>
      <MDBox flex={1} display="flex" sx={{ height: '100%' }}>
        <Paper
          elevation={2}
          sx={{ flex: 1, borderRadius: '10px', p: 2, mb: 1, overflow: 'hidden' }}
        >
          <DataGrid
            apiRef={apiRef}
            rows={models}
            columns={columns}
            initialState={{
              pagination: { paginationModel: { pageSize: 20 } },
              selection: { ids: models && models.length > 0 ? [models[0].id] : [] },
              density: 'compact',
              sorting: {
                sortModel: [{ field: 'provider', sort: 'desc' }]
              }
            }}
            slots={{ toolbar: AiModelCustomToolbar }}
            slotProps={{
              toolbar: {
                onAdd: handleAdd,
                onDelete: handleDelete,
                onDuplicate: handleDuplicate,
                selectedRows
              }
            }}
            loading={modelsLoading}
            pageSizeOptions={[20]}
            disableColumnResize
            checkboxSelection
            disableRowSelectionOnClick
            onRowClick={handleRowClick}
            rowSelectionModel={rowSelectionModel}
            onRowSelectionModelChange={handleRowSelectionModelChange}
            selectionModel={selectionModel}
            onSelectionModelChange={handleSelectionModelChange}
            getRowClassName={(params) => (params.id === selectedRowId ? 'Mui-selected' : '')}
          />
        </Paper>
        <Drawer
          variant="temporary"
          anchor="right"
          open={detailDrawerOpen}
          onClose={toggleDrawer(false)}
          ModalProps={{ keepMounted: false }}
          PaperProps={{
            sx: { width: 700, p: 2 }
          }}
        >
          <AiModelDetails
            model={selectedModel}
            onSubmit={handleFormSubmit}
            onFieldChange={handleFieldChange}
          />
        </Drawer>
        <Dialog open={confirmCloseFormDialogOpen} onClose={handleCancelClose}>
          <DialogTitle>{msg({ id: 'dialog.unsaved_changes.title' })}</DialogTitle>
          <DialogContent>
            <DialogContentText>{msg({ id: 'dialog.unsaved_changes.message' })}</DialogContentText>
          </DialogContent>
          <DialogActions>
            <MDButton onClick={handleCancelClose} color="secondary">
              {msg({ id: 'button.cancel' })}
            </MDButton>
            <MDButton onClick={handleCloseDrawer} color="primary">
              {msg({ id: 'button.discard' })}
            </MDButton>
          </DialogActions>
        </Dialog>
      </MDBox>
    </Fade>
  );

  function clearModelSelected() {
    setSelectionModel([]);
    setRowSelectionModel([]);
    setSelectedModel(null);
    setSelectedRowId(null);
  }

  function handleCreate(data) {
    createAiModel({ ...data })
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.aiModels.create.success' })
        });
        clearModelSelected();
      })
      .catch(() => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.aiModels.create.error' })
        });
      });
  }

  function handleUpdate(data) {
    updateAiModel({
      id: data.id,
      aiModelRequest: { ...data }
    })
      .unwrap()
      .then(() => {
        addNotification({
          type: 'success',
          title: msg({ id: 'notification.success.title' }),
          message: msg({ id: 'page.aiModels.update.success' })
        });
        clearModelSelected();
      })
      .catch(() => {
        addNotification({
          type: 'error',
          title: msg({ id: 'notification.error.title' }),
          message: msg({ id: 'page.aiModels.update.error' })
        });
      });
  }
}
